import { compact, times } from 'lodash'
import { useMemo, useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { useTransientEventBadge } from '../../hooks/useTransientEventBadge'
import { Item } from '../../models/Item'
import { ItemComponent } from '../../models/ItemComponent'
import { Package } from '../../models/Package'
import { Subscription } from '../../models/Subscription'
import { Transaction } from '../../models/Transaction'
import { currencyFloat } from '../../utils/numbers'
import { formatDuration } from '../../utils/numbers'
import { Icon } from '../Icon'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import SegmentedControl from '../app/design/SegmentedControl'
import { ContractSearchableSelect } from './ContractSearchableSelect'
import { TypeSearchableSelect } from './TypeSearchableSelect'
import { ImageView } from '../common/ImageView'
import { Checkbox } from '../common/fields/Checkbox'
import { CurrencyField } from '../common/fields/CurrencyField'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { Select } from '../common/fields/Select'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { SelectedPhoto } from '../workflows/PhotoPicker'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    item: Item
    onClose: () => void
    onDelete?: () => void
    isLoading?: boolean
    enableAllFields?: boolean
    onSave: (item: Item) => void
    componentItems: Item[]
}

export const SubscriptionPlanPage = ({
    item,
    onClose,
    onDelete,
    isLoading,
    onSave,
    enableAllFields = true,
    componentItems,
}: Props) => {
    const owner = PSApplication.owner()

    // we copy these over to the main subscription item because the package item is not editable for some reason?
    const [scratchSubscription, setScratchSubscription] = useState<Item>(
        item.clone({
            capacity: item.aPackage?.capacity,
            capacity_type: item.aPackage?.capacityType ?? 'session',
            components: item.aPackage?.components ?? [],
            package_all: item.aPackage?.packageAll ?? true,
            is_public: item.isPublic ?? true,
        })
    )
    const [selectedPhoto, setSelectedPhoto] = useState<SelectedPhoto | null>(
        item.image?.uri ? { type: 'url', url: item.image.url ?? '' } : null
    )

    const { isBadgeShowing: isServiceItemAddedBadgeShowing, onEvent: onServiceItemAdded } =
        useTransientEventBadge()

    const numDiscountInstanceString = Subscription.instanceStrings(
        Subscription.SUBSCRIPTION_INSTANCES,
        Subscription.MINIMUM_NUMBER_OF_INSTANCES
    )

    const componentItemSearchableItems = useMemo(() => {
        const selectedItemIDs = new Set(
            compact(scratchSubscription.components.map((item) => item.component?.id))
        )

        return compact(
            componentItems.map((item) => {
                if (selectedItemIDs.has(item.id ?? '')) {
                    return null
                }

                return {
                    id: item.id ?? '',
                    title: item.name ?? '',
                    tertiaryView: (
                        <ImageView
                            image={item.image}
                            className="size-9 rounded-full border-2 border-white"
                            fallbackIcon={{
                                icon: 'refresh.small',
                                background: 'round',
                                className: 'size-9',
                            }}
                        />
                    ),
                    item,
                }
            })
        )
    }, [componentItems, scratchSubscription])

    const handleChangeCapacityType = (newCapacityType: string) => {
        if (newCapacityType !== 'session' && newCapacityType !== 'duration') {
            return
        }
        if (scratchSubscription.capacityType === newCapacityType) {
            return
        }

        setScratchSubscription((s) =>
            s.clone({
                components: s.components.map((c) =>
                    c.clone({
                        capacity: undefined,
                    })
                ),
                capacity_type: newCapacityType,
                capacity: undefined,
            })
        )
    }

    const handleSelectServiceItem = (
        searchableItem: (typeof componentItemSearchableItems)[number] | null
    ) => {
        if (searchableItem === null) {
            return
        }

        setScratchSubscription((s) =>
            s.clone({
                components: [...s.components, ItemComponent.initWithItem(searchableItem.item)],
            })
        )

        onServiceItemAdded()
    }

    const canSave = useMemo(() => {
        return (
            Boolean(scratchSubscription.name) &&
            Boolean(scratchSubscription.capacityType) &&
            Boolean(scratchSubscription.capacity) &&
            (scratchSubscription.packageAll || scratchSubscription.components.length > 0) &&
            Boolean(scratchSubscription.frequency) &&
            Boolean(scratchSubscription.interval) &&
            Boolean(scratchSubscription.rate)
        )
    }, [scratchSubscription])

    return (
        <WorkflowPage
            title="Subscription"
            footerContent={
                <div
                    className={twm(
                        'flex w-full items-center gap-x-4',
                        onDelete !== undefined ? 'justify-between' : 'justify-end'
                    )}
                >
                    {onDelete !== undefined && (
                        <Button
                            intent="secondary"
                            className="text-danger-400"
                            onClick={onDelete}
                            isLoading={isLoading}
                        >
                            Delete subscription
                        </Button>
                    )}

                    <Button
                        intent="primary"
                        onClick={() => onSave(scratchSubscription)}
                        disabled={isLoading || !canSave}
                        isLoading={isLoading}
                    >
                        Save
                    </Button>
                </div>
            }
            onClose={onClose}
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="General">
                    {enableAllFields && (
                        <WorkflowPage.PhotoPicker
                            onSelectPhoto={(photoFile) => {
                                setSelectedPhoto({ type: 'file', file: photoFile })
                            }}
                            onClearPhoto={() => setSelectedPhoto(null)}
                            selectedPhoto={selectedPhoto}
                        />
                    )}
                    <WorkflowPage.IconSection
                        title="Name (required)"
                        icon={<Icon icon="field.large" background="round" />}
                    >
                        <TextField
                            isFullWidth
                            size="medium"
                            placeholder="Enter name"
                            onClear={() => setScratchSubscription((s) => s?.clone({ name: '' }))}
                            value={scratchSubscription?.name}
                            onChange={(e) => {
                                setScratchSubscription((s) => s?.clone({ name: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                    <WorkflowPage.IconSection
                        title="Payment amount (required)"
                        description="Charged every payment period"
                        icon={<Icon icon="dollar.large" background="round" />}
                    >
                        <CurrencyField
                            isFullWidth
                            size="medium"
                            placeholder="0.00"
                            value={scratchSubscription?.rate}
                            onClear={() =>
                                setScratchSubscription((s) => s?.clone({ rate: undefined }))
                            }
                            min={0.01}
                            step={0.01}
                            icon={'dollar9.medium'}
                            onChange={(e) => {
                                if (Number.isNaN(e)) {
                                    setScratchSubscription((s) => s?.clone({ rate: undefined }))
                                    return
                                }
                                setScratchSubscription((s) => s?.clone({ rate: e }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                    <WorkflowPage.IconSection
                        title="Setup Fee"
                        description="A one-time fee added to the first subscription payment"
                        icon={<Icon icon="coins2.large" background="round" />}
                    >
                        <CurrencyField
                            isFullWidth
                            size="medium"
                            placeholder="0.00"
                            value={scratchSubscription?.deposit ?? 0}
                            min={0}
                            step={0.01}
                            icon={'dollar9.large'}
                            onClear={() =>
                                setScratchSubscription((s) => s?.clone({ deposit: undefined }))
                            }
                            onChange={(e) => {
                                if (Number.isNaN(e)) {
                                    setScratchSubscription((s) => s?.clone({ deposit: undefined }))
                                    return
                                }
                                setScratchSubscription((s) => s?.clone({ deposit: e }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                    {owner.allowDiscount && (
                        <>
                            <WorkflowPage.IconSection
                                title="Discount rate"
                                icon={<Icon icon="discount.large" background="round" />}
                            >
                                <Select
                                    size="medium"
                                    className="max-h-96 overflow-auto"
                                    selectedOptionID={scratchSubscription?.discount?.toString()}
                                    options={Transaction.discountRateStrings().map((option) => ({
                                        id: option.id ?? '',
                                        title: option.label ?? '',
                                    }))}
                                    onSelectOption={(option) => {
                                        if (option) {
                                            setScratchSubscription((s) =>
                                                s?.clone({ discount: option.id })
                                            )
                                        }
                                    }}
                                />
                            </WorkflowPage.IconSection>
                            <WorkflowPage.IconSection
                                title="Number of discounts"
                                icon={<Icon icon="hash.large" background="round" />}
                            >
                                <Select
                                    size="medium"
                                    className="max-h-96 overflow-auto"
                                    selectedOptionID={
                                        scratchSubscription?.discountInstances?.toString() || ''
                                    }
                                    options={numDiscountInstanceString.map((option) => ({
                                        id: option.id ?? '',
                                        title: option.label ?? 'Continuous',
                                    }))}
                                    onSelectOption={(option) => {
                                        if (option) {
                                            setScratchSubscription((s) =>
                                                s?.clone({ discount_instances: option.id })
                                            )
                                        }
                                    }}
                                />
                            </WorkflowPage.IconSection>
                        </>
                    )}
                    {owner.allowSalesTax && (
                        <WorkflowPage.IconSection
                            title="Allow sales tax"
                            icon={<Icon icon="percentage2.large" background="round" />}
                            hasHoverEffect
                            secondaryView={
                                <Checkbox
                                    isChecked={scratchSubscription?.allowSalesTax}
                                    onChange={(e) =>
                                        setScratchSubscription((s) =>
                                            s?.clone({ allow_salestax: e })
                                        )
                                    }
                                />
                            }
                        />
                    )}
                    <WorkflowPage.IconSection
                        title="Description"
                        icon={<Icon icon="note.large" background="round" />}
                    >
                        <TextArea
                            size="medium"
                            placeholder="Enter description"
                            isAutoResizing
                            value={scratchSubscription?.memo}
                            onChange={(e) => {
                                setScratchSubscription((s) => s?.clone({ memo: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Limit usage">
                    <WorkflowPage.IconSection
                        title="Sessions (required)"
                        icon="hash.large"
                        description="Limit the number of sessions or hours covered by this subscription"
                    >
                        <div className="grid grid-cols-2 grid-rows-1 gap-x-2">
                            <SegmentedControl
                                size="medium"
                                segments={[
                                    { label: 'Sessions', value: 'session' },
                                    { label: 'Hours', value: 'duration' },
                                ]}
                                value={scratchSubscription.capacityType ?? 'session'}
                                onChange={handleChangeCapacityType}
                            />

                            <Select
                                size="medium"
                                options={Item.sessionStrings(
                                    (!scratchSubscription.id &&
                                        scratchSubscription.capacityType === undefined) ||
                                        scratchSubscription.capacityType === 'session',
                                    (!scratchSubscription.id &&
                                        scratchSubscription.capacityType === undefined) ||
                                        scratchSubscription.capacityType === 'duration'
                                ).map((option) => ({
                                    id: option.id ?? '',
                                    title: option.label ?? '',
                                }))}
                                onSelectOption={(option) => {
                                    setScratchSubscription((s) =>
                                        s.clone({
                                            capacity:
                                                option === null
                                                    ? 1
                                                    : option.id.endsWith('h')
                                                      ? parseInt(
                                                            option.id.substring(
                                                                0,
                                                                option.id.length - 1
                                                            )
                                                        )
                                                      : parseInt(option.id),
                                        })
                                    )
                                }}
                                selectedOptionID={
                                    (scratchSubscription.capacityType ?? 'session') === 'session'
                                        ? (scratchSubscription.capacity?.toString() ?? '')
                                        : scratchSubscription.capacity !== undefined
                                          ? `${scratchSubscription.capacity}h`
                                          : ''
                                }
                            />
                        </div>
                    </WorkflowPage.IconSection>

                    {enableAllFields && (
                        <WorkflowPage.IconSection
                            title="Services (required)"
                            icon="tags.large"
                            description="Select the items this package will cover"
                        >
                            <div className="flex w-full flex-col items-stretch justify-start gap-y-3">
                                <SegmentedControl
                                    size="medium"
                                    segments={[
                                        { label: 'All services', value: 'all' },
                                        { label: 'Some services', value: 'some' },
                                    ]}
                                    value={
                                        (scratchSubscription.packageAll ?? true) ? 'all' : 'some'
                                    }
                                    onChange={(newValue) =>
                                        setScratchSubscription((s) =>
                                            s.clone({
                                                package_all: newValue === 'all',
                                            })
                                        )
                                    }
                                />

                                {!scratchSubscription.packageAll && (
                                    <>
                                        <div className="flex items-center justify-between">
                                            <Label size="md">Services</Label>

                                            {isServiceItemAddedBadgeShowing && (
                                                <Label
                                                    size="sm"
                                                    className="text-success-400 font-medium"
                                                >
                                                    Added
                                                </Label>
                                            )}
                                        </div>

                                        <SearchableSelect
                                            items={componentItemSearchableItems}
                                            onSelectItem={handleSelectServiceItem}
                                            titleWeight="bold"
                                            placeholder="Type to search"
                                            shouldRefocusAfterSelection
                                        />

                                        {scratchSubscription.components.map(
                                            (itemComponent, itemComponentIndex) => {
                                                const item = itemComponent.component
                                                if (item === undefined) {
                                                    return null
                                                }

                                                const imageURI = item.image?.uri

                                                const handleRemove = () => {
                                                    setScratchSubscription((s) =>
                                                        s.clone({
                                                            components: s.components.filter(
                                                                (component) =>
                                                                    component.component?.id !==
                                                                    item.id
                                                            ),
                                                        })
                                                    )
                                                }

                                                const handleSelectDropdownOption = (option: {
                                                    id: string
                                                }) => {
                                                    setScratchSubscription((s) =>
                                                        s.clone({
                                                            components: s.components.map(
                                                                (component, componentIndex) =>
                                                                    componentIndex ===
                                                                    itemComponentIndex
                                                                        ? component.clone({
                                                                              capacity: !!option.id
                                                                                  ? parseInt(
                                                                                        option.id
                                                                                    )
                                                                                  : undefined,
                                                                          })
                                                                        : component
                                                            ),
                                                        })
                                                    )
                                                }

                                                return (
                                                    <WorkflowPage.RemovableItemContainer
                                                        key={item.id ?? ''}
                                                        onRemove={handleRemove}
                                                    >
                                                        <WorkflowPage.SelectedItem
                                                            title={item.name}
                                                            subtitle={compact([
                                                                formatDuration(
                                                                    item.duration ?? null
                                                                ),
                                                                item.rate === undefined
                                                                    ? 'TBD'
                                                                    : `$${currencyFloat(item.rate)}`,
                                                            ]).join(' · ')}
                                                            tertiaryView={
                                                                item.iconConfig ===
                                                                undefined ? undefined : item
                                                                      .iconConfig.type ===
                                                                  'image' ? (
                                                                    <img
                                                                        src={imageURI}
                                                                        className="size-8 rounded-full border-2 border-white"
                                                                    />
                                                                ) : (
                                                                    <Icon
                                                                        icon={item.iconConfig.icon}
                                                                        background="round"
                                                                        className="size-8"
                                                                    />
                                                                )
                                                            }
                                                            primaryDropdownOptions={[
                                                                {
                                                                    id: '',
                                                                    title: 'Same as package',
                                                                },
                                                                ...times(100, (index) => ({
                                                                    id: (
                                                                        (index + 1) *
                                                                        (scratchSubscription.capacityType ===
                                                                        'duration'
                                                                            ? 60
                                                                            : 1)
                                                                    ).toString(),
                                                                    title:
                                                                        Package.capacityDisplayString(
                                                                            scratchSubscription.capacityType ??
                                                                                'session',
                                                                            index + 1
                                                                        ) ?? '',
                                                                })),
                                                            ]}
                                                            onSelectPrimaryDropdownOption={
                                                                handleSelectDropdownOption
                                                            }
                                                            selectedPrimaryDropdownOptionID={
                                                                itemComponent.capacity?.toString() ??
                                                                ''
                                                            }
                                                            primaryDropdownClassName="w-[180px]"
                                                            primaryDropdownAlignment="end"
                                                        />
                                                    </WorkflowPage.RemovableItemContainer>
                                                )
                                            }
                                        )}
                                    </>
                                )}
                            </div>
                        </WorkflowPage.IconSection>
                    )}
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Subscription details">
                    <WorkflowPage.IconSection
                        title="Auto-charge frequency (required)"
                        description="Select when a client should be automatically charged"
                        icon={<Icon icon="repeat.large" background="round" />}
                    >
                        <Select
                            size="medium"
                            selectedOptionID={`${scratchSubscription?.interval ?? 0}${
                                scratchSubscription?.frequency === 'daily'
                                    ? 'd'
                                    : scratchSubscription?.frequency === 'weekly'
                                      ? 'w'
                                      : 'm'
                            }`}
                            options={Subscription.frequencyStrings().map((option) => ({
                                id: option.id ?? '',
                                title: option.label ?? '',
                            }))}
                            onSelectOption={(option) => {
                                if (option) {
                                    setScratchSubscription((s) =>
                                        s?.clone(Subscription.parseFrequencyString(option.id))
                                    )
                                }
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Number of payments"
                        description="Number of times the client will be automatically charged"
                        icon={<Icon icon="hash.large" background="round" />}
                    >
                        <Select
                            size="medium"
                            selectedOptionID={scratchSubscription?.instances?.toString() || ''}
                            options={numDiscountInstanceString.map((option) => ({
                                id: option.id ?? '',
                                title: option.label ?? 'Continuous',
                            }))}
                            onSelectOption={(option) => {
                                if (option) {
                                    setScratchSubscription((s) =>
                                        s?.clone({ instances: option.id })
                                    )
                                }
                            }}
                        />
                    </WorkflowPage.IconSection>

                    {owner.allowWidget && (
                        <WorkflowPage.IconSection
                            title="Show online"
                            description="Product will appear the clients after booking you online"
                            icon={<Icon icon="computerClock.large" background="round" />}
                            hasHoverEffect
                            secondaryView={
                                <Checkbox
                                    isChecked={scratchSubscription?.isPublic}
                                    onChange={(e) => {
                                        setScratchSubscription((s) => s?.clone({ is_public: e }))
                                    }}
                                />
                            }
                        />
                    )}
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Attachments">
                    <WorkflowPage.IconSection
                        title="Attach a contract"
                        description="This contract will be signed when a client purchases this service"
                        icon={<Icon icon="pen.large" background="round" />}
                    >
                        <ContractSearchableSelect
                            selectedContract={scratchSubscription?.contract}
                            onSelectContract={(c) => {
                                setScratchSubscription((s) => s?.clone({ contract: c }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Attach a form"
                        description="This form will be completed when a client purchases this service"
                        icon={<Icon icon="field.large" background="round" />}
                    >
                        <TypeSearchableSelect
                            owner={owner}
                            selectedForm={scratchSubscription?.form}
                            onSelectForm={(f) => {
                                setScratchSubscription((s) => s?.clone({ form: f }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>
            </div>
        </WorkflowPage>
    )
}
